/**
 * About Us Page - VERSION 1 REVISED: Accurate to Current Capabilities
 */

import React from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  Grid,
  Button
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const AboutUs = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const handleGenerateClick = () => {
    if (isAuthenticated) {
      // Already logged in - go straight to Quick Themes
      navigate('/quick-themes');
    } else {
      // Not logged in - go to signup
      navigate('/register');
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f9fafb', py: 8 }}>
      <Container maxWidth="md">
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography 
            variant="h2" 
            sx={{ 
              fontWeight: 800, 
              mb: 2,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}
          >
            About GetWordExPress
          </Typography>
          <Typography variant="h5" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
            Your own WordPress theme, built in minutes — no developer needed
          </Typography>
        </Box>

        <Paper sx={{ p: 5 }}>
          {/* The Problem */}
          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mb: 3 }}>
            Here's the Problem
          </Typography>
          
          <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}>
            You need a professional website. Your options? Pay a developer $3,000+ and wait 6 weeks, 
            or spend your weekends fighting with WordPress themes that look like they're from 2015.
          </Typography>

          <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}>
            Both options suck. So we built a third one.
          </Typography>

          {/* The Solution */}
          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mt: 5, mb: 3 }}>
            Our Solution
          </Typography>

          <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}>
            GetWordExPress builds custom WordPress themes to fit your business. Pick your industry,
            customize your preferences, and get a complete theme in under 15 minutes — ready to install, no coding needed.
          </Typography>

          <Grid container spacing={3} sx={{ my: 3 }}>
            {[
              'Complete themes with 7+ pages',
              'Industry-specific content and layouts',
              'Mobile responsive design',
              'Clean, modern styling and typography',
              'Ready to upload to WordPress',
              'No coding required'
            ].map((feature, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CheckCircleIcon sx={{ color: '#16a34a', mr: 2 }} />
                  <Typography variant="body1">{feature}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

          {/* Why We're Different */}
          <Box sx={{ my: 5, p: 4, bgcolor: '#f0f9ff', borderRadius: 2 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Why We're Different
            </Typography>
            <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}>
              <strong>You only pay when generation succeeds.</strong> We generate your theme first and run 
              quality validation. If generation completes successfully and passes our technical checks, 
              you pay $39-79. If generation fails or has technical issues? No charge, with free retries.
            </Typography>
          </Box>

          {/* How It Works */}
          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mt: 5, mb: 3 }}>
            How It Works
          </Typography>

          <Grid container spacing={3} sx={{ my: 2 }}>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center', p: 3 }}>
                <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
                  1. Generate
                </Typography>
                <Typography variant="body1">
                  Select your industry and preferences. Your theme is assembled to fit your business.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center', p: 3 }}>
                <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
                  2. Validate
                </Typography>
                <Typography variant="body1">
                  We automatically check for technical issues and WordPress compatibility.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center', p: 3 }}>
                <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
                  3. Pay & Download
                </Typography>
                <Typography variant="body1">
                  If generation succeeds, complete payment and download your theme files.
                </Typography>
              </Box>
            </Grid>
          </Grid>

          {/* Who We Serve */}
          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mt: 5, mb: 3 }}>
            Who This Is For
          </Typography>

          <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}>
            <strong>Small business owners</strong> who need professional websites without the professional budget. 
            Lawyers, doctors, contractors, restaurants, consultants—anyone who wants to establish credible 
            online presence without breaking the bank.
          </Typography>

          <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}>
            <strong>Agencies and freelancers</strong> who want to deliver client work faster and more profitably.
          </Typography>

          <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}>
            <strong>Anyone</strong> tired of choosing between expensive developers and cheap-looking DIY solutions.
          </Typography>

          {/* Pricing */}
          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mt: 5, mb: 3 }}>
            Simple, Fair Pricing
          </Typography>

          <Grid container spacing={3} sx={{ my: 2 }}>
            <Grid item xs={12} md={6}>
              <Box sx={{ p: 3, border: '2px solid #e5e7eb', borderRadius: 2 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Quick Themes - $39
                </Typography>
                <Typography variant="body1">
                  Choose from 25 pre-built templates across different industries. 
                  Professional themes ready in 5-10 minutes.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ p: 3, border: '2px solid #667eea', borderRadius: 2 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Custom Themes - $149
                </Typography>
                <Typography variant="body1">
                  A complete theme built around your industry — your pages, your content, your layout.
                  Ready in 10-15 minutes.
                </Typography>
              </Box>
            </Grid>
          </Grid>

          {/* Simple Promise */}
          <Box sx={{ mt: 5, p: 4, bgcolor: '#f0fdf4', borderRadius: 2, textAlign: 'center' }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Our Promise
            </Typography>
            <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}>
              Quality themes, fair prices, no surprises. Generate first, pay only when it works. 
              Failed generations get free retries or full refunds.
            </Typography>
          </Box>

          {/* CTA */}
          <Box sx={{ mt: 5, textAlign: 'center' }}>
            <Button
              variant="contained"
              size="large"
              startIcon={<RocketLaunchIcon />}
              onClick={handleGenerateClick}
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 600
              }}
            >
              Generate Your First Theme
            </Button>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Start generating risk-free. Pay only when generation succeeds.
            </Typography>
          </Box>

          {/* Contact */}
          <Box sx={{ mt: 5, pt: 4, borderTop: 1, borderColor: 'divider', textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Questions?{' '}
              <a href="/contact" style={{ color: '#667eea', textDecoration: 'none', fontWeight: 600 }}>
                Get in touch
              </a>
              . We respond to every message.
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default AboutUs;